<template><div></div></template>

<script setup lang="ts">
import { VueQueryPlugin } from "@tanstack/vue-query";
import { type App, createApp, onMounted, onUnmounted } from "vue";

import { createEventListener } from "@/lib/composables/event-listener";
import type { Newsletter } from "@/types/newsletter";

import type {
  SubscribeFormVariant,
  SubscriberFacingAuthentication,
} from "./SubscribeForm/lib";
import SubscribeFormContainer from "./SubscribeForm/SubscribeFormContainer.vue";

const SELECTORS = [".subscribe-form", "#subscribe-form"];

type Instance = { app: App; sentinel: Node };
const instances = new Map<Element, Instance>();

const props = defineProps<{
  newsletter: Newsletter;
  subscriberFacingAuthentication: SubscriberFacingAuthentication;
  variant: SubscribeFormVariant;
}>();

const renderSubscribeForm = (target: Element): Instance => {
  const app = createApp(SubscribeFormContainer, {
    newsletter: props.newsletter,
    subscriberFacingAuthentication: props.subscriberFacingAuthentication,
    variant: props.variant,
  });

  app.use(VueQueryPlugin);
  app.mount(target);

  // Insert a sentinel node to detect if the content gets morphed away.
  // Checking el.isConnected alone isn't enough - nanomorph might keep
  // the container but replace its children.
  const sentinel = document.createElement("span");
  target.appendChild(sentinel);

  return { app, sentinel };
};

onMounted(() => {
  for (const selector of SELECTORS) {
    for (const el of document.querySelectorAll(selector)) {
      const instance = renderSubscribeForm(el);
      instances.set(el, instance);
    }
  }

  createEventListener(window, "message", (ev) => {
    if (ev.data !== "preview:morphed") {
      return;
    }

    // Remove apps whose content was morphed away
    for (const [el, { app, sentinel }] of instances) {
      if (!sentinel.isConnected) {
        try {
          app.unmount();
        } catch {
          // App may already be in a bad state
        }

        instances.delete(el);
      }
    }

    // Mount apps for any new elements
    for (const selector of SELECTORS) {
      for (const el of document.querySelectorAll(selector)) {
        if (!instances.has(el)) {
          const instance = renderSubscribeForm(el);
          instances.set(el, instance);
        }
      }
    }
  });

  onUnmounted(() => {
    for (const [, { app }] of instances) {
      try {
        app.unmount();
      } catch {
        // App may already be in a bad state
      }
    }
  });
});
</script>
